/**
 * One collapsible site card inside the multi-site Sources layout.
 *
 * Used by <SourcesPage /> when the host implements `useSitesList()`.
 * Each section runs its own `useSourceList` query scoped to a single
 * site, so per-site caching stays in react-query and individual sites
 * can collapse/expand without affecting siblings.
 *
 * Visual pattern (chevron + name + count header, expandable body
 * containing a DataTable) mirrors the per-site grouping in SMS's
 * ProtocolsList / ResourcesList.
 *
 * Socket-driven live merges are intentionally not handled here. AIM is
 * the only host that ships vendor socket events, and AIM stays on
 * single-site mode (where SourcesPage owns its own merge). Source
 * mutations (create/update/delete) invalidate every site's query via
 * `invalidateSources`, so add/edit/archive flows refresh correctly in
 * multi-site mode without a custom merge.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Center, Flex, Spinner, Text } from '@chakra-ui/react';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi2';
import { DataTable, type Column } from '@themes/aegix';
import { useSourceList } from '../hooks/useSources';
import type { VendorSite } from '../types/vendor';
import type { SourcesSiteSummary } from '../types/host';

export interface SourcesSiteSectionProps {
  site: SourcesSiteSummary;
  /** Already-debounced search query (shared across all sections). */
  debouncedQuery: string;
  /** Global status filter from the page-level toggle. */
  statusFilter: 'all' | 'active' | 'archived';
  /** Whether the section is expanded on mount. */
  defaultExpanded?: boolean;
  columns: Column<VendorSite>[];
  renderRowActions: (row: VendorSite) => React.ReactNode;
  onRowClick?: (row: VendorSite) => void;
}

export function SourcesSiteSection({
  site,
  debouncedQuery,
  statusFilter,
  defaultExpanded = false,
  columns,
  renderRowActions,
  onRowClick,
}: SourcesSiteSectionProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // 'all' must be sent explicitly — omitting the param means
  // active-only on the backend (the safe default for legacy callers).
  const deletedParam: boolean | 'all' =
    statusFilter === 'all' ? 'all' : statusFilter === 'archived';

  const { sources, isLoading, isFetching } = useSourceList({
    school: site.id,
    query: debouncedQuery,
    offset: 0,
    limit: 100,
    deleted: deletedParam,
  });

  const toggle = () => setIsExpanded((v) => !v);

  return (
    <Box
      mb={3}
      border="1px solid"
      borderColor="border.secondary"
      borderRadius="lg"
      overflow="hidden"
      data-e2e={`sourcesSiteSection-${site.id}`}
    >
      <Flex
        px={4}
        py={3}
        alignItems="center"
        justifyContent="space-between"
        cursor="pointer"
        bg="surface.secondary"
        _hover={{ bg: 'surface.tertiary' }}
        onClick={toggle}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        data-e2e={`sourcesSiteHeader-${site.id}`}
      >
        <Flex alignItems="center" gap={2}>
          {isExpanded ? <HiChevronDown size={18} /> : <HiChevronRight size={18} />}
          <Text fontWeight={600} fontSize="sm" color="text.primary">
            {site.name}
          </Text>
        </Flex>
        <Text fontSize="xs" color="text.secondary">
          {t('sources_count', { count: sources.length })}
        </Text>
      </Flex>

      {isExpanded && (
        <Box>
          {isLoading ? (
            <Center py={6}>
              <Spinner size="sm" color="button.primary" />
            </Center>
          ) : sources.length === 0 ? (
            <Center py={6}>
              <Text fontSize="sm" color="text.secondary">
                {isFetching
                  ? t('loading_ellipsis')
                  : debouncedQuery
                    ? t('no_sources_match_the_search')
                    : t('no_sources_for_this_site')}
              </Text>
            </Center>
          ) : (
            <DataTable
              columns={columns}
              data={sources}
              keyExtractor={(row) => String(row.id)}
              actionsPosition="start"
              renderRowActions={renderRowActions}
              onRowClick={onRowClick}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export default SourcesSiteSection;
